import moment from 'moment';
import { IScope, ITimeoutService, ILocationService } from 'angular';
import { IScreenRecordingBridgeClient } from '../../serviceworkerBridgeClients/IScreenRecordingBridgeClient';
import type { ITelemetryBeaconService } from '../../services/core/TelemetryBeaconService';
import { forceDigest } from '../../utils/angularDigest';

// Define interfaces for services (since they're JavaScript services, we use minimal typing)
interface IBridgeRuntimeService {
    uploadResponse(targetFilename: string, apiUrl: string, fileData?: Blob, deleteTemporaryResponseDataOnSuccess?: boolean, recoveryEntryId?: number): angular.IPromise<void>;
    quit(restart?: boolean, openFileExplorer?: boolean): void;
}

interface IDataBridgeService {
    packResponse(fileName: string, recoveryMetadata?: any): angular.IPromise<{ content?: Blob; recoveryEntryId?: number; [key: string]: any }>;
    waitForPendingWrites(timeoutMs?: number): Promise<void>;
}

interface IUserInfo {
    getFirstName(): string;
    getLastName(): string;
    getSchoolCode(): string | undefined;
    getCandidateNo(): string | undefined;
    getTaskUUID(): string;
}

interface IUserInfoService {
    getUserInfo(): IUserInfo;
}

interface ITask {
    shortLabel: string;
    [key: string]: any;
}

interface ITaskInfo {
    getTaskDataByUUID(uuid: string): ITask;
    isMediaAssessment?(): boolean;
    getPackageType(): string;
    getSubject(): string | undefined;
    getUploadConfig?(): { response_upload_api_url?: string; fallbackURL?: string } | null;
    getAssessmentUuid?(): string;
}

interface ITaskInfoService {
    getTaskInfo(): ITaskInfo;
}

interface IPWAService {
    getResponseUploadApiUrl?(): string | null;
    getCurrentMetadata?(): { assessment_uuid?: string; response_upload_api_url?: string; hybrid_response_api_url?: string } | null;
}

interface IUibModalService {
    [key: string]: any;
}

// Extend IScope with custom properties
interface TaskDoneScope extends IScope {
    progress: number;
    showProgressBar: boolean;
    attempts: number;
    uploading: boolean;
    packResponseSuccess?: boolean;
    packResponseError?: boolean;
    uploadResponseSuccess?: boolean;
    uploadResponseError?: boolean;
    startedUpload?: boolean;
    message?: string;
    showAttemptUploadBtn: boolean;
    isAutoRetrying?: boolean;
    uploadResponse: (retries?: number, fileData?: Blob) => void;
    quit: (openFileDialog?: boolean) => void;
    canQuit: () => boolean;
}

/**
 * TaskDoneController class handles the logic for managing task completion and response uploading.
 * It interacts with various services to retrieve user and task information, manage screen recording,
 * package responses, and handle the upload process.
 */
export class TaskDoneController {
    FILENAME_PREFIX: string = 'IB';
    SEP: string = '_';
    EXT: string = '.ibresponse';
    max_attempts: number = 3;
    forceQuit: boolean = false;
    sourceFilename: string = '';
    targetFilename: string = '';
    packedResponseData: Blob | null = null; // Store the packed response data for retry attempts
    private taskUUID: string = '';
    private uploadCompleteKey: string = '';
    private recoveryEntryId: number | undefined = undefined;

    specialCharMap: { [key: string]: string } = {
        'ä': 'ae',
        'ö': 'oe',
        'ü': 'ue',
        'Ä': 'AE',
        'Ö': 'OE',
        'Ü': 'UE',
        'ß': 'ss',
        'é': 'e',
        'è': 'e',
        'â': 'a',
        'á': 'a',
        'à': 'a',
        'í': 'i',
        'ì': 'i',
        'ï': 'i'
    };

    private $scope: TaskDoneScope;
    private $timeout: ITimeoutService;
    private BridgeRuntimeService: IBridgeRuntimeService;
    private DataBridgeService: IDataBridgeService;
    private UserInfoService: IUserInfoService;
    private TaskInfoService: ITaskInfoService;
    private $uibModal: IUibModalService;
    private $location: ILocationService;
    private ScreenRecordingBridgeClient: IScreenRecordingBridgeClient;
    private PWAService: IPWAService;
    private TelemetryBeaconService: ITelemetryBeaconService;

    private buildUploadCompleteKey(user: IUserInfo): string {
        const parts = [
            this.taskUUID,
            user.getCandidateNo() || '',
            user.getSchoolCode() || '',
            user.getFirstName() || '',
            user.getLastName() || '',
        ];

        return `ib_uploadComplete_${parts.map((part) => this.cleanTarget(String(part))).join('_')}`;
    }

    static $inject = [
        '$scope',
        '$timeout',
        'BridgeRuntimeService',
        'DataBridgeService',
        'UserInfoService',
        'TaskInfoService',
        '$uibModal',
        '$location',
        'ScreenRecordingBridgeClient',
        'PWAService',
        'TelemetryBeaconService'
    ];

    /**
     * Constructor for TaskDoneController.
     */
    constructor(
        $scope: TaskDoneScope,
        $timeout: ITimeoutService,
        BridgeRuntimeService: IBridgeRuntimeService,
        DataBridgeService: IDataBridgeService,
        UserInfoService: IUserInfoService,
        TaskInfoService: ITaskInfoService,
        $uibModal: IUibModalService,
        $location: ILocationService,
        ScreenRecordingBridgeClient: IScreenRecordingBridgeClient,
        PWAService: IPWAService,
        TelemetryBeaconService: ITelemetryBeaconService
    ) {
        this.$scope = $scope;
        this.$timeout = $timeout;
        this.BridgeRuntimeService = BridgeRuntimeService;
        this.DataBridgeService = DataBridgeService;
        this.UserInfoService = UserInfoService;
        this.TaskInfoService = TaskInfoService;
        this.$uibModal = $uibModal;
        this.$location = $location;
        this.ScreenRecordingBridgeClient = ScreenRecordingBridgeClient;
        this.PWAService = PWAService;
        this.TelemetryBeaconService = TelemetryBeaconService;

        this.init();
    }

    preventRouteChanges(): void {
        this.$scope.$on('$routeChangeStart', (e: angular.IAngularEvent, next: any, current: any) => {
            if (this.canQuit()) {
                return;
            }
            if (next.$$route && next.$$route.controller !== 'TaskDoneController') {
                console.log('TaskDoneController routeChangeStart -> disallowed', e, next, current);
                e.preventDefault();
                return false;
            }
        });
    }

    /**
     * Initializes the controller by setting up the necessary scope variables and starting the response handling process.
     */
    init(): void {
        const user = this.UserInfoService.getUserInfo();
        const task_info = this.TaskInfoService.getTaskInfo();
        const task = task_info.getTaskDataByUUID(user.getTaskUUID());

        this.taskUUID = user.getTaskUUID();

        // Check if the task is a media assessment and redirect if necessary.
        if (task_info.isMediaAssessment && typeof task_info.isMediaAssessment === 'function' && task_info.isMediaAssessment()) {
            this.$location.path('/mediaassessmenttaskdone/');
            return;
        } else {
            this.preventRouteChanges();
        }

        // Pattern 3 guard: if this assessment was already uploaded in this session,
        // skip pack+upload and show success state directly
        this.uploadCompleteKey = this.buildUploadCompleteKey(user);
        let alreadyUploaded = false;
        try {
            alreadyUploaded = !!sessionStorage.getItem(this.uploadCompleteKey);
        } catch (e) {
            alreadyUploaded = false;
        }
        if (alreadyUploaded) {
            this.$scope.packResponseSuccess = true;
            this.$scope.uploadResponseSuccess = true;
            this.$scope.showAttemptUploadBtn = false;
            this.$scope.progress = 100;
            this.forceQuit = true;
            if (this.TelemetryBeaconService) {
                this.TelemetryBeaconService.record('taskdone.skipped_already_uploaded', 'info', {
                    taskUUID: this.taskUUID,
                });
            }
            this.$scope.quit = this.quit.bind(this);
            this.$scope.canQuit = this.canQuit.bind(this);
            return;
        }

        // Determine if the attempt upload button should be shown.
        this.$scope.showAttemptUploadBtn = task_info.getPackageType() !== 'specimen';

        // Construct the filename prefix based on the task subject.
        if (task_info && task_info.getSubject()) {
            const subject = task_info.getSubject()!.replace(/[^a-z0-9]/gi, '_');
            this.FILENAME_PREFIX = this.FILENAME_PREFIX + this.SEP + subject;
        }

        // Construct the source and target filenames.
        this.sourceFilename = this.FILENAME_PREFIX + this.SEP +
            this.clean(user.getFirstName()) + this.SEP +
            this.clean(user.getLastName()) + this.SEP +
            (user.getSchoolCode() && user.getSchoolCode()!.length > 0 ? user.getSchoolCode() + this.SEP : '') +
            task.shortLabel + this.SEP +
            (user.getCandidateNo() && user.getCandidateNo()!.length > 0 ? user.getCandidateNo() + this.SEP : '') +
            moment().format('DDMMMYY-HHmm') + 'h' + this.EXT;

        this.targetFilename = this.FILENAME_PREFIX + this.SEP +
            this.cleanTarget(user.getFirstName()) + this.SEP +
            this.cleanTarget(user.getLastName()) + this.SEP +
            (user.getSchoolCode() && user.getSchoolCode()!.length > 0 ? user.getSchoolCode() + this.SEP : '') +
            task.shortLabel + this.SEP +
            (user.getCandidateNo() && user.getCandidateNo()!.length > 0 ? user.getCandidateNo() + this.SEP : '') +
            moment().format('DDMMMYY-HHmm') + 'h' + this.EXT;

        // Initialize progress and state variables
        this.$scope.progress = 0;
        this.$scope.showProgressBar = false;
        this.$scope.attempts = 0;
        this.$scope.uploading = false;
        this.$scope.isAutoRetrying = false;

        // Stop screen recording, pack the response, and handle the upload process.
        this.stopScreenRecording()
            .catch((error: any) => {
                console.error("Error while waiting for stopRecording:", error);
            })
            .finally(async () => {
                console.log('Stopped screen recording, waiting for pending writes');
                try {
                    await this.DataBridgeService.waitForPendingWrites(10000);
                } catch (e) {
                    console.error('Error waiting for pending writes:', e);
                }
                console.log('Packing response');

                // Build recovery metadata so the SW can create a
                // PendingRecoveryEntry before packing starts.
                let apiUrl = this.getApiUrl();
                let assessmentUuid = this.getAssessmentUuid();

                // Konami: when FAILPACK or FAILUPLOAD is used and the package has no
                // upload URL or assessment UUID, fall back to a synthetic value so the
                // recovery entry is still created and the recovery flow can be tested.
                // Real values are preserved when they exist.
                const firstName = user.getFirstName()?.toUpperCase();
                const isKonami = firstName === 'FAILPACK' || firstName === 'FAILUPLOAD';
                if (isKonami && (!apiUrl || !assessmentUuid)) {
                    console.warn('TaskDoneController: Konami active but upload metadata incomplete — filling in test fallbacks');
                    apiUrl = apiUrl || 'https://konami-test.invalid/pwa-api/response-upload/';
                    assessmentUuid = assessmentUuid || this.taskUUID || 'konami-test-uuid';
                }

                let recoveryMetadata: import('../../../../../serviceworker/src/bridges/responseDataBridgeMessages').PackRecoveryMetadata | undefined;
                if (apiUrl && assessmentUuid && assessmentUuid.length > 0) {
                    recoveryMetadata = {
                        apiUrl,
                        assessmentUuid,
                        targetFilename: this.targetFilename,
                        candidateInfo: {
                            firstName: user.getFirstName(),
                            lastName: user.getLastName(),
                            schoolCode: user.getSchoolCode(),
                            candidateNo: user.getCandidateNo(),
                        },
                        subject: task_info.getSubject(),
                        taskUUID: this.taskUUID,
                    };
                } else {
                    console.warn('TaskDoneController: Cannot create recovery metadata — apiUrl:', apiUrl, 'assessmentUuid:', assessmentUuid);
                    if (this.TelemetryBeaconService) {
                        this.TelemetryBeaconService.record('recovery.metadata_missing', 'warning', {
                            hasApiUrl: String(!!apiUrl),
                            hasAssessmentUuid: String(!!assessmentUuid),
                        });
                    }
                }

                this.DataBridgeService.packResponse(this.sourceFilename, recoveryMetadata)
                    .then((response: { content?: Blob; recoveryEntryId?: number; [key: string]: any }) => {
                        this.$scope.packResponseSuccess = true;

                        // Store recovery entry ID so it can be passed to the upload call
                        this.recoveryEntryId = response.recoveryEntryId;

                        // Store the packed response data for potential retry attempts
                        if (response && response.content) {
                            this.packedResponseData = response.content;
                        }

                        console.log('Response packed, now triggering upload');
                        this.uploadResponse(2, this.packedResponseData);
                    })
                    .catch((error: any) => {
                        this.handlePackResponseError(error);
                    });
            }); // finally

        // Set a timeout to force quit after 5 minutes.
        this.$timeout(() => {
            this.forceQuit = true;
            if (this.TelemetryBeaconService) {
                this.TelemetryBeaconService.record('upload.force_quit', 'critical', {
                    attempts: String(this.$scope.attempts),
                    uploadSuccess: String(!!this.$scope.uploadResponseSuccess),
                    uploadError: String(!!this.$scope.uploadResponseError),
                    uploading: String(!!this.$scope.uploading),
                    // A force-quit is the worst outcome in the flow: without the file
                    // and task it names, the row cannot be traced to a response.
                    targetFilename: this.targetFilename || '',
                    taskUUID: this.taskUUID || '',
                });
            }
        }, 60 * 5 * 1000); // 5 minutes — allows 2 min for screen recordings + 3 min for pack+upload

        // Bind methods to the scope.
        this.$scope.uploadResponse = this.uploadResponse.bind(this);
        this.$scope.quit = this.quit.bind(this);
        this.$scope.canQuit = this.canQuit.bind(this);
    }

    /**
     * Stops the screen recording.
     * @returns Promise that resolves when the screen recording is stopped.
     */
    stopScreenRecording(): Promise<void> {
        return this.ScreenRecordingBridgeClient.stopRecording();
    }

    private handlePackResponseError(error: any): void {
        console.error('Error packing response:', error);

        // Set scope state synchronously. The rejection originates from a SW
        // message callback outside Angular's zone, so we schedule a digest via
        // forceDigest() (raw setTimeout + $apply) which bypasses Sentry/OpenReplay
        // instrumentation that can otherwise swallow $evalAsync/$timeout.
        this.$scope.packResponseError = true;
        this.$scope.packResponseSuccess = false;
        this.$scope.uploading = false;
        this.$scope.showProgressBar = false;
        this.$scope.isAutoRetrying = false;
        this.forceQuit = true;
        forceDigest(this.$scope);
    }

    /**
     * Reset all upload state to prepare for a new upload attempt.
     * Using a separate method to ensure consistent state management.
     */
    resetUploadState(): void {
        // Safely update scope variables without forcing another digest cycle
        // Set all upload state variables to their initial values
        this.$scope.uploading = false;
        this.$scope.showProgressBar = false;
        this.$scope.uploadResponseSuccess = undefined;
        this.$scope.uploadResponseError = undefined;
        this.$scope.isAutoRetrying = false;
        this.$scope.progress = 0;
    }

    /**
     * Uploads the response data with retries.
     * @param retries - The number of retries allowed for the upload (defaults to 2 if not provided).
     * @param fileData - The file data to be uploaded (uses stored packedResponseData if not provided).
     */
    uploadResponse(retries?: number, fileData?: Blob): void {
        // Use stored data if fileData is not provided (e.g., when called from retry button)
        if (!fileData && this.packedResponseData) {
            fileData = this.packedResponseData;
        }

        // Use default retries if not provided
        if (retries === undefined || retries === null) {
            retries = 2;
        }

        // Validate that we have file data before proceeding
        if (!fileData) {
            console.warn('Cannot upload: No file data available');
            if ((window as any).Sentry) { (window as any).Sentry.captureMessage('Upload failed: No file data available', { level: 'error', tags: { context: 'response_upload' } }); }
            this.$scope.uploadResponseError = true;
            this.$scope.uploadResponseSuccess = false;
            return;
        }

        if (this.canUpload()) {
            // Reset all upload state completely
            this.resetUploadState();

            // Now set the new upload state - do this in a new digest cycle
            this.$timeout(() => {
                this.$scope.startedUpload = true;
                this.$scope.uploading = true;
                this.$scope.showProgressBar = true;

                const upload = (): angular.IPromise<void> => {
                    this.$scope.attempts++;

                    const apiUrl = this.getApiUrl();

                    if (!apiUrl) {
                        console.warn('No upload API URL available - cannot proceed with upload');
                        if ((window as any).Sentry) { (window as any).Sentry.captureMessage('Upload failed: No API URL available', { level: 'error', tags: { context: 'response_upload' } }); }
                        this.$scope.uploadResponseError = true;
                        this.$scope.uploadResponseSuccess = false;
                        this.$scope.uploading = false;
                        this.$scope.showProgressBar = false;
                        return Promise.reject(new Error('No upload API URL available')) as angular.IPromise<void>;
                    }

                    const promise = this.BridgeRuntimeService.uploadResponse(
                        this.targetFilename,
                        apiUrl,
                        fileData,
                        true,
                        this.recoveryEntryId
                    );

                    promise.then(() => {
                        // Persist upload success for page reload/wake scenarios
                        try {
                            sessionStorage.setItem(this.uploadCompleteKey, String(Date.now()));
                        } catch (e) {
                            console.warn('Failed to persist upload-complete flag to sessionStorage:', e);
                        }
                        this.$timeout(() => {
                            this.$scope.uploadResponseSuccess = true;
                            this.$scope.uploadResponseError = false;
                            this.$scope.isAutoRetrying = false;
                            this.$scope.progress = 100;
                            this.$scope.showProgressBar = false;
                            this.$scope.uploading = false;
                        });
                    }, () => {
                        // Don't set uploadResponseError here - let the retry logic handle it
                        // This allows us to show "retrying" message instead of "failed" during auto-retries
                    }, (progressResult: { progress: number | string; message?: string }) => {
                        this.$timeout(() => {
                            this.$scope.progress = parseInt(String(progressResult.progress), 10);
                            this.$scope.message = progressResult.message;
                        });
                    });

                    return promise;
                };

                // Function to retry upload with decreasing retries count
                const retry = (maxRetries: number): void => {
                    // Validate maxRetries is a valid integer
                    if (!(Number.isInteger ? Number.isInteger(maxRetries) : (typeof maxRetries === 'number' && maxRetries % 1 === 0))) {
                        maxRetries = 0;
                    }

                    upload().catch((error: any) => {
                        if (maxRetries > 0) {
                            // We're going to auto-retry, so show the retrying message
                            // Keep progress bar visible during retry
                            this.$timeout(() => {
                                this.$scope.isAutoRetrying = true;
                                this.$scope.uploadResponseError = false; // Clear error state during retry
                                this.$scope.showProgressBar = true; // Ensure progress bar stays visible
                                this.$scope.uploading = true; // Keep uploading state true during retry
                                // Reset progress to show we're starting a new attempt, but keep bar visible
                                this.$scope.progress = 0;
                            });

                            // Wait 2 seconds before retrying
                            this.$timeout(() => {
                                // Ensure progress bar is still visible when starting new retry
                                this.$scope.showProgressBar = true;
                                this.$scope.uploading = true;
                                retry(maxRetries - 1);
                            }, 2000);
                            return;
                        }

                        // No more retries, finalize the error state
                        this.$timeout(() => {
                            this.$scope.isAutoRetrying = false;
                            this.$scope.uploadResponseError = true;
                            this.$scope.uploadResponseSuccess = false; // Set to false to hide spinner and show X icon
                            this.$scope.uploading = false;
                            this.$scope.showProgressBar = false;
                        });
                        console.error('Upload failed after all retries', error);
                        if (this.TelemetryBeaconService) {
                            this.TelemetryBeaconService.record('upload.exhausted', 'critical', {
                                attempts: String(this.$scope.attempts),
                                errorMessage: error instanceof Error ? error.message.substring(0, 200) : String(error).substring(0, 200),
                                targetFilename: this.targetFilename || '',
                                taskUUID: this.taskUUID || '',
                            });
                        }
                    }).finally(() => {
                        if (maxRetries <= 0) {
                            // Only set uploading to false on final attempt
                            this.$scope.uploading = false;
                        }
                    });
                };

                // Start the retry process
                retry(retries);
            }, 500); // Small delay to ensure previous state is cleared
        }
    }

    /**
     * Checks if the response can be uploaded.
     * @returns True if the response can be uploaded, false otherwise.
     */
    canUpload(): boolean {
        return !this.$scope.uploading && !this.$scope.uploadResponseSuccess;
    }

    /**
     * Quits the application.
     * @param openFileDialog - Whether to open a file dialog before quitting.
     */
    quit(openFileDialog?: boolean): void {
        this.BridgeRuntimeService.quit(false, openFileDialog);
    }

    /**
     * Checks if the application can be quit.
     * @returns True if the application can be quit, false otherwise.
     */
    canQuit(): boolean {
        return !!this.$scope.uploadResponseSuccess || this.forceQuit || (this.$scope.attempts >= this.max_attempts);
    }

    /**
     * Checks if the auto upload has finally failed.
     * @returns True if the auto upload has failed, false otherwise.
     */
    isAutoUploadFinallyFailed(): boolean {
        if (this.forceQuit && !this.$scope.uploadResponseSuccess) {
            return true;
        }

        if (this.$scope.uploadResponseSuccess || this.$scope.uploading) {
            return false;
        }

        if (this.$scope.attempts >= this.max_attempts) {
            return true;
        }

        return false;
    }

    /**
     * Resolves the response upload API URL from PWAService or legacy TaskInfoService.
     */
    private getApiUrl(): string | null {
        if (this.PWAService?.getResponseUploadApiUrl && typeof this.PWAService.getResponseUploadApiUrl === 'function') {
            const url = this.PWAService.getResponseUploadApiUrl();
            if (url) return url;
        }
        if (this.TaskInfoService.getTaskInfo().getUploadConfig) {
            const config = this.TaskInfoService.getTaskInfo().getUploadConfig!();
            if (config) {
                return config.response_upload_api_url || config.fallbackURL || null;
            }
        }
        return null;
    }

    /**
     * Gets the assessment UUID for recovery metadata + telemetry correlation.
     * Source of truth is PWAService.currentMetadata.assessment_uuid (persisted
     * to localStorage on assessment load — see PWAService.setCurrentMetadata).
     * TaskInfo does not declare an assessmentUuid accessor via
     * readables/accessables, so taskInfo.getAssessmentUuid() is only a safety
     * net for any code path that attaches it dynamically.
     */
    private getAssessmentUuid(): string {
        // Primary source: PWAService's current assessment metadata.
        if (this.PWAService && typeof this.PWAService.getCurrentMetadata === 'function') {
            const metadata = this.PWAService.getCurrentMetadata();
            if (metadata && metadata.assessment_uuid) {
                return metadata.assessment_uuid;
            }
        }
        // Fallback: legacy TaskInfo accessor — may not exist in this codebase.
        const taskInfo = this.TaskInfoService.getTaskInfo();
        if (taskInfo.getAssessmentUuid && typeof taskInfo.getAssessmentUuid === 'function') {
            return taskInfo.getAssessmentUuid() || '';
        }
        return '';
    }

    clean(str: string): string {
        return str.replace(/\s/g, '_').replace(/[^a-zÄÖÜäöüß0-9_]/gi, '');
    }

    cleanTarget(str: string): string {
        let result = str;
        for (const c in this.specialCharMap) {
            result = result.replace(new RegExp(c, 'g'), this.specialCharMap[c]);
        }
        return result.replace(/\s/, '_').replace(/[^a-z0-9_]/gi, '');
    }
}


export default function registerTaskDoneController(angular: angular.IAngularStatic): void {
    const module = angular.module('assessmentFrontendControllers');
    // Use explicit array syntax to match original JavaScript version
    const injectArray: any[] = [...TaskDoneController.$inject, TaskDoneController];
    module.controller('TaskDoneController', injectArray);
}
